import { openDB, IDBPDatabase } from 'idb';

const DB_NAME = 'ChartDataDB';
const DB_VERSION = 1;
const STORE_NAME = 'chartData';

interface ChartDataRecord {
  entityId: number;
  year: number;
  month: number;
  recordType: string;
  data: any;
  timestamp?: number;
  id?: string;
  reference?: string;
}

class IndexedDBService {
  private dbPromise: Promise<IDBPDatabase>;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase> {
    return openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('reportingObjectModelRecords  ', 'entityId', { unique: false });
        }
      }
    });
  }

  async saveChartData(
    entityId: number,
    year: number,
    month: number,
    recordType: string,
    data: any,
    reference: string
  ): Promise<void> {
    const db = await this.dbPromise;
    const id = `${entityId}_${year}_${month}_${recordType}`;

    await db.put(STORE_NAME, {
      id,
      entityId,
      year,
      month,
      recordType,
      data,
      timestamp: Date.now(),
      reference: reference
    });
  }

  async getChartData(
    entityId: number,
    year: number,
    month: number,
    recordType: string
  ): Promise<any | null> {
    const db = await this.dbPromise;
    const id = `${entityId}_${year}_${month}_${recordType}`;

    return db.get(STORE_NAME, id);
  }

  /**
   * Get all data from IndexedDB for multiple entities and months, and aggregate it
   * @param entityIds Array of entity IDs to get data for
   * @param recordType The type of record to get data for
   * @returns Aggregated data from all entities and months
   */
  async getAggregatedData(entityIds: number[], recordType: string): Promise<any> {
    // Get all data from IndexedDB for the specified entities and record type
    const allData = {};

    // For each entity
    for (const entityId of entityIds) {
      // For the past 12 months
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

      for (let i = 0; i < 12; i++) {
        // Calculate year and month
        let month = currentMonth - i;
        let year = currentYear;

        if (month <= 0) {
          month += 12;
          year -= 1;
        }

        // Get data from IndexedDB
        const data = await this.getChartData(entityId, year, month, recordType);
        if (data) allData[data.id] = data;
      }
    }

    return allData;
  }
}

export const indexedDBService = new IndexedDBService();
